import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, LogOut, Store, Zap, Receipt, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    const q = e.target.q.value.trim();
    navigate(q ? `/browse?q=${encodeURIComponent(q)}` : "/browse");
  };

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
        <Link to="/" data-testid="brand-logo" className="font-display font-black text-xl tracking-tighter flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#FF007F]" strokeWidth={2.5} />
          <span>GLOW<span className="text-[#FF007F] text-glow-pink">CART</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm">
          <NavLink data-testid="nav-browse" to="/browse" className={({isActive}) => `uppercase tracking-widest text-xs ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>Browse</NavLink>
          <NavLink data-testid="nav-categories" to="/categories" className={({isActive}) => `uppercase tracking-widest text-xs ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>Categories</NavLink>
          <NavLink data-testid="nav-sell" to="/seller" className={({isActive}) => `uppercase tracking-widest text-xs ${isActive ? "text-[#05D9E8]" : "text-zinc-400 hover:text-[#05D9E8]"}`}>Sell</NavLink>
        </nav>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="nav-search-input"
            name="q"
            placeholder="Search the glow..."
            className="input-dark pl-11 text-sm w-full"
          />
        </form>

        <div className="flex items-center gap-3 ml-auto md:ml-0">
          <Link data-testid="nav-cart" to="/cart" className="relative p-2 hover:text-[#FF007F] transition-colors">
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[#FF007F] text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center font-bold px-1 glow-pink">{count}</span>
            )}
          </Link>

          {user ? (
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <Link data-testid="nav-admin" to="/admin" className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-widest text-[#CCFF00] hover:text-white">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link data-testid="nav-orders" to="/orders" className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-300 hover:text-white">
                <Receipt className="w-4 h-4" /> Orders
              </Link>
              <Link data-testid="nav-seller-dashboard" to="/seller" className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-300 hover:text-white">
                <Store className="w-4 h-4" /> Dashboard
              </Link>
              <button data-testid="nav-logout-btn" onClick={logout} className="p-2 hover:text-[#FF007F]" title="Logout">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              <Link data-testid="nav-login-link" to="/login" className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white">Login</Link>
              <Link data-testid="nav-register-link" to="/register" className="btn-primary !py-2 !px-4 !text-xs">Join</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
